import type { ReceiveCurrencyType } from 'BreetHelpers';
import { useCryptoSettingsApi } from 'BreetHooks';
import { closeModal, useAppDispatch } from 'BreetRedux';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

export const useCryptoSettings = (isModal?: boolean) => {
	const dispatch = useAppDispatch();

	const { currentReceiveCurrency, fiatMeta, isLoading, manageUSD, isUpdating } = useCryptoSettingsApi();

	const [selectedReceiveCurrency, setSelectedReceiveCurrency] = useState<ReceiveCurrencyType | undefined>(currentReceiveCurrency);

	const saveChangesRef = useRef<HTMLButtonElement>(null);
	const navigate = useNavigate();
	const location = useLocation();
	const locationFrom = (location.state as { from?: string } | null)?.from;

	useEffect(() => {
		setSelectedReceiveCurrency(currentReceiveCurrency);
	}, [currentReceiveCurrency]);

	const hasUnsavedChanges = selectedReceiveCurrency !== currentReceiveCurrency;

	const handleCurrencyChange = useCallback((currencyType: ReceiveCurrencyType) => {
		setSelectedReceiveCurrency(currencyType);
		saveChangesRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, []);

	const handleSaveChanges = useCallback(() => {
		if (selectedReceiveCurrency && hasUnsavedChanges) {
			manageUSD({
				receiveCurrency: selectedReceiveCurrency,
				onSuccess: () => {
					if (locationFrom) {
						navigate(locationFrom)?.catch(console.error);
					}

					if (isModal) {
						dispatch(closeModal('cryptoSettings'));
						dispatch(closeModal('settlementWallet'));
					}
				},
			});
		}
	}, [selectedReceiveCurrency, hasUnsavedChanges, manageUSD, locationFrom, isModal, navigate, dispatch]);

	return {
		handleCurrencyChange,
		handleSaveChanges,
		hasUnsavedChanges,
		isUpdating,
		isLoading,
		selectedReceiveCurrency,
		saveChangesRef,
		fiatMeta,
		currentReceiveCurrency,
	};
};
